import { nriAuthorityData, nriWhatsappUrl } from "../../data/nriTaxData";
import { Instagram, Youtube, ArrowUpRight, Quote } from "lucide-react";

const keyStats = [
  { value: "10+", label: "Years" },
  { value: "1,000+", label: "NRI Returns" },
  { value: "40+", label: "Countries" },
  { value: "24K+", label: "Followers" },
];

export const NriAuthoritySection = () => (
  <section id="nri-authority" className="relative py-28" style={{ background: "#1a1c1b" }}>
    <div className="absolute top-0 left-0 right-0 h-px bg-[#3f4816]/30" />
    <div className="max-w-[87.5rem] mx-auto px-10">

      {/* Heading */}
      <div className="mb-14">
        <span className="inline-block text-[#888680] text-xs font-medium uppercase tracking-[0.2em] mb-5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
          {nriAuthorityData.sectionLabel}
        </span>
        <p className="text-[#888680]/60 font-semibold uppercase mb-1" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(0.85rem, 0.7rem + 0.6vw, 1rem)", letterSpacing: "-0.01em" }}>
          CA Rahul Taneja
        </p>
        <h2 className="font-black uppercase leading-tight" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.8rem, 1.4rem + 2.5vw, 3.5rem)", letterSpacing: "-0.03em" }}>
          <span className="text-[#d9fb06]">Dubai-Based</span>
          <br />
          <span className="text-[#dfddd6]">NRI Tax Specialist</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        {/* Text */}
        <div>
          {/* Key stats badges */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {keyStats.map((s, i) => (
              <div key={i} className="bg-[#302f2c]/50 border border-[#3f4816]/20 rounded-sm p-4 text-center">
                <p className="text-[#d9fb06] font-black" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "1.5rem", letterSpacing: "-0.03em" }}>{s.value}</p>
                <p className="text-[#888680] text-[10px] uppercase tracking-wide mt-0.5" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{s.label}</p>
              </div>
            ))}
          </div>

          <Quote size={18} className="text-[#3f4816]/50 mb-4" />
          <div className="flex flex-col gap-3 mb-8">
            <p className="text-[#dfddd6] font-bold text-base leading-snug" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              I file NRI returns from{" "}
              <span className="text-[#d9fb06]">inside Dubai.</span>
            </p>
            <p className="text-[#888680] text-sm leading-relaxed" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              Not a remote filing service. Not a form-filler. A{" "}
              <span className="text-[#dfddd6] font-semibold">cross-border tax strategist</span>{" "}
              who has spent 10+ years at the intersection of{" "}
              <span className="text-[#dfddd6] font-semibold">Indian tax law</span>{" "}
              and the financial reality of Indians living abroad.
            </p>
            <p className="text-[#888680] text-sm" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              I also hold a{" "}
              <span className="text-[#dfddd6] font-semibold">UAE SCA Financial Influencer License</span>{" "}
              — one of the few CAs with regulated standing on both sides of the border.
            </p>
          </div>

          {/* Credential badges */}
          <div className="flex flex-wrap gap-2 mb-6">
            {nriAuthorityData.credentials.map((c, i) => (
              <span key={i} className="text-[#d9fb06] text-[11px] font-black uppercase tracking-wider border border-[#d9fb06]/30 bg-[#d9fb06]/8 px-3 py-1.5 rounded-full" style={{ fontFamily: "'Inter', Arial, sans-serif", backgroundColor: "rgba(217, 251, 6, 0.08)" }}>
                {c}
              </span>
            ))}
          </div>

          <p className="text-[#888680]/60 text-xs mb-4" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{nriAuthorityData.socialProof}</p>
          <div className="flex items-center gap-3">
            <a href={nriAuthorityData.instagram} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#888680] text-xs border border-[#3f4816]/30 px-4 py-2 rounded-full transition-all hover:border-[#3f4816]/60 hover:text-[#dfddd6]">
              <Instagram size={13} /> Instagram <ArrowUpRight size={10} />
            </a>
            <a href={nriAuthorityData.youtube} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#888680] text-xs border border-[#3f4816]/30 px-4 py-2 rounded-full transition-all hover:border-[#3f4816]/60 hover:text-[#dfddd6]">
              <Youtube size={13} /> YouTube <ArrowUpRight size={10} />
            </a>
          </div>
        </div>

        {/* Photo + Case Study */}
        <div className="flex flex-col items-center gap-7">
          <div className="relative">
            <div className="absolute -inset-3 rounded-full border border-[#3f4816]/20" />
            <div className="w-60 h-60 rounded-full overflow-hidden border-2 border-[#3f4816]/40">
              <img src={nriAuthorityData.photo} alt="CA Rahul Taneja" className="w-full h-full object-cover object-top" loading="lazy" />
            </div>
          </div>

          {/* Case study — numbers only */}
          <div className="w-full bg-[#302f2c]/50 border border-[#d9fb06]/15 rounded-sm p-6">
            <p className="text-[#d9fb06] text-[10px] font-black uppercase tracking-widest mb-3" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              {nriAuthorityData.caseStudy.location}
            </p>
            <p className="text-[#888680] text-xs mb-4" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{nriAuthorityData.caseStudy.client}</p>
            <p className="text-[#888680] text-xs mb-4 leading-relaxed" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{nriAuthorityData.caseStudy.story}</p>
            <div className="bg-[#1a1c1b] border border-[#3f4816]/20 rounded-sm p-4 mb-3">
              <p className="text-[#888680] text-xs mb-1" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{nriAuthorityData.caseStudy.result}</p>
              <p className="text-[#d9fb06] font-black" style={{ fontFamily: "'Inter', Arial, sans-serif", fontSize: "clamp(1.2rem, 1rem + 0.8vw, 1.5rem)", letterSpacing: "-0.02em" }}>
                {nriAuthorityData.caseStudy.savings}
              </p>
            </div>
            <p className="text-[#888680]/40 text-[11px] italic" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>{nriAuthorityData.caseStudy.note}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
